import { useState } from "react";
import { toast } from "sonner";
import { Store, Truck, Undo2 } from "lucide-react";
import { useApi, errMsg, euro } from "@/lib/api";

export const PickupBox = ({ order, onDone }) => {
  const call = useApi();
  const mark = async (status) => {
    try {
      await call("patch", `/admin/orders/${order.id}`, { status });
      toast.success(status === "enviado" ? (order.customer.email ? "Listo para recoger · email al cliente" : "Listo para recoger · avisa al cliente por teléfono") : "Pedido marcado como recogido");
      onDone();
      [4000, 9000].forEach((ms) => setTimeout(onDone, ms));
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4 border bg-white p-3 space-y-2" data-testid={`admin-pickup-box-${order.number}`}>
      <p className="font-semibold flex items-center gap-2"><Store className="w-4 h-4" /> Recogida en tienda</p>
      <p className="text-xs text-slate-500">Avisar al <a href={`tel:${order.customer.phone}`} className="font-semibold text-brand">{order.customer.phone}</a>{order.customer.email ? " (también por email)" : " (sin email: llamar o WhatsApp)"}.</p>
      {order.status === "pagado" && <button onClick={() => mark("enviado")} className="btn-primary !h-9 w-full" data-testid={`admin-pickup-ready-${order.number}`}>Marcar listo para recoger{order.customer.email ? " y avisar al cliente" : ""}</button>}
      {order.status === "enviado" && <button onClick={() => mark("entregado")} className="btn-outline !h-9 w-full" data-testid={`admin-pickup-done-${order.number}`}>Marcar como recogido</button>}
      {order.shipping_email && order.shipping_email !== "skipped" && <p className="text-xs text-slate-500" data-testid={`admin-pickup-email-${order.number}`}>Email de aviso: {order.shipping_email === "sent" ? "enviado" : order.shipping_email === "failed" ? "falló" : "enviando…"}</p>}
    </div>
  );
};

export const RETURN_STATUS = {
  solicitada: { label: "Devolución solicitada", cls: "bg-amber-100 text-amber-700" },
  aceptada: { label: "Devolución aceptada", cls: "bg-blue-100 text-blue-700" },
  recibida: { label: "Devolución recibida", cls: "bg-indigo-100 text-indigo-700" },
  reembolsada: { label: "Reembolsada", cls: "bg-emerald-100 text-emerald-700" },
  rechazada: { label: "Devolución rechazada", cls: "bg-rose-100 text-rose-700" },
};

export const ShippingBox = ({ order, onDone }) => {
  const call = useApi();
  const [carrier, setCarrier] = useState(order.carrier || "");
  const [tracking, setTracking] = useState(order.tracking || "");
  const ship = async () => {
    try {
      await call("patch", `/admin/orders/${order.id}`, { status: "enviado", carrier, tracking });
      toast.success(order.status === "enviado" ? "Seguimiento guardado" : "Pedido marcado como enviado · email al cliente");
      onDone();
      [4000, 9000, 15000].forEach((ms) => setTimeout(onDone, ms));
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4 border bg-white p-3 space-y-2" data-testid={`admin-ship-box-${order.number}`}>
      <p className="font-semibold flex items-center gap-2"><Truck className="w-4 h-4" /> Envío</p>
      <div className="flex gap-2">
        <input className="field !h-9" placeholder="Empresa (ej. Correos)" value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid={`admin-ship-carrier-${order.number}`} />
        <input className="field !h-9" placeholder="Nº seguimiento (opcional)" value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid={`admin-ship-tracking-${order.number}`} />
      </div>
      <button onClick={ship} className="btn-primary !h-9 w-full" data-testid={`admin-ship-button-${order.number}`}>
        {order.status === "enviado" ? "Guardar seguimiento" : "Marcar enviado y avisar al cliente"}
      </button>
      {order.shipping_email && <p className="text-xs text-slate-500" data-testid={`admin-ship-email-${order.number}`}>Email de envío: {order.shipping_email === "sent" ? "enviado" : order.shipping_email === "failed" ? "falló" : "enviando…"}</p>}
    </div>
  );
};

export const ReturnBox = ({ order, onDone }) => {
  const call = useApi();
  const r = order.return_request;
  if (!r) return null;
  const change = async (status) => {
    try { await call("patch", `/admin/orders/${order.id}/return`, { status }); toast.success("Devolución actualizada"); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="sm:col-span-2 border border-amber-300 bg-amber-50 p-4" data-testid={`admin-return-${order.number}`}>
      <div className="flex flex-wrap items-center gap-3">
        <p className="font-semibold flex items-center gap-2"><Undo2 className="w-4 h-4" /> {r.wants === "cambio" ? "Cambio" : "Devolución"} solicitado el {new Date(r.created_at).toLocaleDateString("es-ES")}</p>
        <select value={r.status} onChange={(e) => change(e.target.value)} className="ml-auto h-9 border rounded-full px-3 text-sm bg-white" data-testid={`admin-return-status-${order.number}`}>
          {Object.entries(RETURN_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>
      {r.items.map((i, k) => <p key={k} className="text-sm mt-2">{i.quantity}× {i.name} (T. {i.size}{i.color && `, ${i.color}`}) — {euro(i.price * i.quantity)}</p>)}
      <p className="text-sm mt-2"><b>Motivo:</b> {r.reason}</p>
      {r.exchange_note && <p className="text-sm"><b>Cambio por:</b> {r.exchange_note}</p>}
      <p className="text-sm"><b>Forma:</b> {r.method === "tienda" ? "En tienda" : "Mensajería / correo"}</p>
    </div>
  );
};
